import React, { useState, useCallback } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";

// ==========================================
// Crisp SVGs for Icons
// ==========================================

export const OpenInterpreterProviderIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0284c7"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
);

const FormatAlignIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="15" y2="12" />
    <line x1="3" y1="18" x2="18" y2="18" />
  </svg>
);

const CodeBracketsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ==========================================
// Types & Defaults
// ==========================================

export interface OpenInterpreterTargetConfigValues {
  model: string;
  autoRun: boolean;
  safeMode: "off" | "ask" | "auto";
  maxOutput?: number | string;
  systemMessage?: string;
  apiBase?: string;
  apiKey?: string;
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
}

export const DEFAULT_OPEN_INTERPRETER_TARGET_CONFIG: OpenInterpreterTargetConfigValues = {
  model: "gpt-4o",
  autoRun: true,
  safeMode: "off",
  maxOutput: 2000,
  systemMessage: "",
  apiBase: "",
  apiKey: "",
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const OPEN_INTERPRETER_EXAMPLE_CONFIG = {
  temperature: 0.7,
  max_tokens: 2048,
  auto_run: true,
  safe_mode: "off",
};

export const OPEN_INTERPRETER_EXAMPLES_LIST = [
  {
    code: '{\n  "temperature": 0.7,\n  "max_tokens": 2048,\n  "auto_run": true,\n  "safe_mode": "off"\n}',
    desc: "- Automated code execution with default gpt-4o parameters",
  },
  {
    code: '{\n  "api_base": "http://localhost:11434/v1",\n  "model": "ollama/codellama",\n  "auto_run": true\n}',
    desc: "- Local execution with Ollama or private LLM gateway",
  },
  {
    code: '{\n  "safe_mode": "ask",\n  "max_output": 2000,\n  "offline": false\n}',
    desc: "- Restricted execution with confirmation mode enabled",
  },
];

// ==========================================
// Formatting & Validation Helpers
// ==========================================

export function formatOpenInterpreterConfigJson(jsonStr: string): { formatted?: string; error?: string } {
  if (!jsonStr || !jsonStr.trim()) {
    return { formatted: "{}" };
  }
  try {
    const parsed = JSON.parse(jsonStr);
    return { formatted: JSON.stringify(parsed, null, 2) };
  } catch (err: any) {
    return { error: "Invalid JSON: " + (err?.message || "Syntax error") };
  }
}

export function validateOpenInterpreterTargetConfig(values: OpenInterpreterTargetConfigValues): {
  isValid: boolean;
  errors: {
    model?: string;
    configJson?: string;
    prompts?: string;
  };
} {
  const errors: {
    model?: string;
    configJson?: string;
    prompts?: string;
  } = {};

  if (!values.model || !values.model.trim()) {
    errors.model = "Model identifier is required";
  }

  if (values.configJson && values.configJson.trim()) {
    try {
      JSON.parse(values.configJson);
    } catch (err: any) {
      errors.configJson = "Invalid JSON configuration: " + (err?.message || "Syntax error");
    }
  }

  if (!values.prompts || values.prompts.length === 0 || values.prompts.every((p) => !p.trim())) {
    errors.prompts = "At least one prompt is required";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// ==========================================
// Component Props
// ==========================================

export interface OpenInterpreterTargetConfigurationProps {
  targetName?: string;
  values: OpenInterpreterTargetConfigValues;
  onChange: (updates: Partial<OpenInterpreterTargetConfigValues>) => void;
  onBack: () => void;
  onNext: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
}

// ==========================================
// Styles
// ==========================================

const pageWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding-bottom: 80px;
`;

const pageHeaderTitle = css`
  font-size: 1.55rem;
  font-weight: 750;
  color: #0f172a;
  margin: 0 0 10px 0;
  letter-spacing: -0.02em;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const providerHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const providerBadgeTitle = css`
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;

  .theme--dark & {
    color: #f1f5f9 !important;
  }
`;

const formCardContainer = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const formGroup = css`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const fieldLabel = css`
  font-size: 0.88rem;
  font-weight: 650;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 4px;

  .theme--dark & {
    color: #cbd5e1 !important;
  }
`;

const textInput = (hasError?: boolean) => css`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  font-size: 0.92rem;
  border: 1.5px solid ${hasError ? "#ef4444" : "#cbd5e1"};
  border-radius: 7px;
  color: #0f172a;
  background: #ffffff;
  outline: none;
  transition: all 0.15s ease-in-out;

  &:focus {
    border-color: ${hasError ? "#ef4444" : "#0284c7"};
    box-shadow: 0 0 0 3px ${hasError ? "rgba(239, 68, 68, 0.15)" : "rgba(2, 132, 199, 0.15)"};
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: ${hasError ? "#ef4444" : "#475569"} !important;
  }
`;

const selectStyle = (hasError?: boolean) => css`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  font-size: 0.92rem;
  border: 1.5px solid ${hasError ? "#ef4444" : "#cbd5e1"};
  border-radius: 7px;
  color: #0f172a;
  background: #ffffff;
  outline: none;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:focus {
    border-color: ${hasError ? "#ef4444" : "#0284c7"};
    box-shadow: 0 0 0 3px ${hasError ? "rgba(239, 68, 68, 0.15)" : "rgba(2, 132, 199, 0.15)"};
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: ${hasError ? "#ef4444" : "#475569"} !important;
  }
`;

const checkboxRow = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  cursor: pointer;
`;

const checkboxInput = css`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #0284c7;
`;

const fieldHelpText = css`
  font-size: 0.81rem;
  color: #64748b;
  margin: 0;
  line-height: 1.45;

  a {
    color: #0284c7;
    text-decoration: underline;
    &:hover {
      color: #0369a1;
    }
  }

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const errorMessageText = css`
  font-size: 0.81rem;
  color: #ef4444;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 550;
`;

const formatButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 0.84rem;
  font-weight: 650;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #475569 !important;
    &:hover {
      background: #334155 !important;
      color: #ffffff !important;
    }
  }
`;

const examplesBox = css`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;

  .theme--dark & {
    border-color: #334155 !important;
    background: #1e293b !important;
  }
`;

const examplesTrigger = (isOpen: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  background: ${isOpen ? "#f1f5f9" : "#f8fafc"};
  border: none;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 650;
  color: #475569;
  text-align: left;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  .theme--dark & {
    background: ${isOpen ? "#334155" : "#1e293b"} !important;
    color: #cbd5e1 !important;
    &:hover {
      background: #334155 !important;
      color: #ffffff !important;
    }
  }
`;

const examplesContent = css`
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const exampleSectionTitle = css`
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 6px;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const exampleList = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const exampleListItem = css`
  font-size: 0.84rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;

  pre {
    background: #f1f5f9;
    color: #0284c7;
    padding: 4px 8px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.78rem;
    font-weight: 600;
    border: 1px solid #e2e8f0;
    margin: 0;
    white-space: pre-wrap;
  }

  .theme--dark & {
    color: #cbd5e1 !important;
    pre {
      background: #1e293b !important;
      color: #38bdf8 !important;
      border-color: #334155 !important;
    }
  }
`;

const jsonEditorContainer = (hasError?: boolean) => css`
  border: 1.5px solid ${hasError ? "#ef4444" : "#e2e8f0"};
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  transition: border-color 0.15s ease-in-out;

  .theme--dark & {
    border-color: ${hasError ? "#ef4444" : "#334155"} !important;
    background: #0f172a !important;
  }
`;

const jsonEditorHeaderBar = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const codeTextarea = css`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #0f172a;
  background: #ffffff;
  border: none;
  outline: none;
  resize: vertical;
  min-height: 120px;

  .theme--dark & {
    background: #0f172a !important;
    color: #f8fafc !important;
  }
`;

const accordionSection = css`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const accordionTrigger = (isOpen: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  background: ${isOpen ? "#f8fafc" : "#ffffff"};
  border: none;
  cursor: pointer;
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
  text-align: left;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: #f8fafc;
  }

  .theme--dark & {
    background: ${isOpen ? "#1e293b" : "#0f172a"} !important;
    color: #f1f5f9 !important;
    &:hover {
      background: #1e293b !important;
    }
  }
`;

const accordionBody = css`
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .theme--dark & {
    border-color: #334155 !important;
  }
`;

const promptItemRow = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const promptInput = (hasError?: boolean) => css`
  flex: 1;
  padding: 9px 12px;
  font-size: 0.9rem;
  border: 1.5px solid ${hasError ? "#ef4444" : "#cbd5e1"};
  border-radius: 6px;
  color: #0f172a;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: ${hasError ? "#ef4444" : "#0284c7"};
    box-shadow: 0 0 0 3px ${hasError ? "rgba(239, 68, 68, 0.15)" : "rgba(2, 132, 199, 0.15)"};
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: ${hasError ? "#ef4444" : "#475569"} !important;
  }
`;

const iconButton = (variant: "danger" | "primary") => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid ${variant === "danger" ? "#fca5a5" : "#cbd5e1"};
  background: ${variant === "danger" ? "#fef2f2" : "#ffffff"};
  color: ${variant === "danger" ? "#ef4444" : "#475569"};
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: ${variant === "danger" ? "#fee2e2" : "#f1f5f9"};
    color: ${variant === "danger" ? "#dc2626" : "#0f172a"};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .theme--dark & {
    background: ${variant === "danger" ? "#450a0a" : "#1e293b"} !important;
    border-color: ${variant === "danger" ? "#7f1d1d" : "#475569"} !important;
    color: ${variant === "danger" ? "#f87171" : "#cbd5e1"} !important;
  }
`;

const actionsFooterRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
  margin-top: 10px;

  .theme--dark & {
    border-color: #334155 !important;
  }
`;

// ==========================================
// Component Implementation
// ==========================================

export function OpenInterpreterTargetConfiguration({
  targetName = "support-agent",
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
}: OpenInterpreterTargetConfigurationProps) {
  // Collapsible accordion states
  const [examplesExpanded, setExamplesExpanded] = useState(false);
  const [testGenExpanded, setTestGenExpanded] = useState(false);
  const [delayExpanded, setDelayExpanded] = useState(false);
  const [extHookExpanded, setExtHookExpanded] = useState(false);

  // Field validation and format errors
  const [errors, setErrors] = useState<{
    model?: string;
    configJson?: string;
    prompts?: string;
  }>({});
  const [jsonFormatError, setJsonFormatError] = useState<string | null>(null);

  const handleFormatJson = useCallback(() => {
    const res = formatOpenInterpreterConfigJson(values.configJson);
    if (res.error) {
      setJsonFormatError(res.error);
    } else if (res.formatted) {
      onChange({ configJson: res.formatted });
      setJsonFormatError(null);
    }
  }, [values.configJson, onChange]);

  const handleLoadExampleJson = useCallback((exampleObj: any) => {
    onChange({ configJson: JSON.stringify(exampleObj, null, 2) });
    setJsonFormatError(null);
    setExamplesExpanded(false);
  }, [onChange]);

  // Prompt actions
  const handlePromptChange = (index: number, newText: string) => {
    const updated = [...values.prompts];
    updated[index] = newText;
    onChange({ prompts: updated });
    if (errors.prompts) setErrors((prev) => ({ ...prev, prompts: undefined }));
  };

  const handleAddPrompt = () => {
    onChange({ prompts: [...values.prompts, "{{prompt}}"] });
  };

  const handleRemovePrompt = (index: number) => {
    if (values.prompts.length <= 1) return;
    const updated = values.prompts.filter((_, i) => i !== index);
    onChange({ prompts: updated });
  };

  const handleNextClick = () => {
    const result = validateOpenInterpreterTargetConfig(values);
    if (!result.isValid) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    onNext();
  };

  return (
    <div css={pageWrapperStyle}>
      {/* 1. Header Section */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "8px" }}>
        <div>
          <Heading level={1} css={pageHeaderTitle}>
            {`Configure Target: ${targetName}`}
          </Heading>
          <div css={providerHeaderRow}>
            <OpenInterpreterProviderIcon />
            <span css={providerBadgeTitle}>Open Interpreter</span>
          </div>
        </div>
        {renderModeSwitcher && (
          <div style={{ flexShrink: 0, marginTop: "4px" }}>
            {renderModeSwitcher}
          </div>
        )}
      </div>

      {/* 2. Main Configuration Form Card */}
      <div css={formCardContainer}>
        {/* Field 1: Model Identifier */}
        <div css={formGroup}>
          <label css={fieldLabel} htmlFor="open-interpreter-model-input">
            Model Identifier <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            id="open-interpreter-model-input"
            type="text"
            css={textInput(!!errors.model)}
            placeholder="gpt-4o"
            value={values.model}
            onChange={(e) => {
              onChange({ model: e.target.value });
              if (errors.model) {
                setErrors((prev) => ({ ...prev, model: undefined }));
              }
            }}
          />
          {errors.model && (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {errors.model}
            </p>
          )}
          <p css={fieldHelpText}>
            Underlying language model used by Open Interpreter (e.g. gpt-4o, claude-3-5-sonnet, or ollama/codellama).
          </p>
        </div>

        {/* Field 2: API Base & API Key Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "18px",
          }}
        >
          {/* API Base */}
          <div css={formGroup}>
            <label css={fieldLabel} htmlFor="open-interpreter-api-base-input">
              API Base URL
            </label>
            <input
              id="open-interpreter-api-base-input"
              type="text"
              css={textInput()}
              placeholder="http://localhost:11434/v1"
              value={values.apiBase || ""}
              onChange={(e) => onChange({ apiBase: e.target.value })}
            />
            <p css={fieldHelpText}>
              Optional API base URL for local servers (Ollama, vLLM, LocalAI) or reverse proxies.
            </p>
          </div>

          {/* API Key */}
          <div css={formGroup}>
            <label css={fieldLabel} htmlFor="open-interpreter-api-key-input">
              API Key
            </label>
            <input
              id="open-interpreter-api-key-input"
              type="password"
              css={textInput()}
              placeholder="sk-..."
              value={values.apiKey || ""}
              onChange={(e) => onChange({ apiKey: e.target.value })}
            />
            <p css={fieldHelpText}>
              Provider API key (e.g. OpenAI or Anthropic). Leave empty to use environment variables.
            </p>
          </div>
        </div>

        {/* Field 3: Auto Run & Safe Mode Row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "18px",
            alignItems: "center",
          }}
        >
          {/* Auto Run */}
          <div css={formGroup}>
            <label css={fieldLabel}>Execution Mode</label>
            <label css={checkboxRow} htmlFor="open-interpreter-auto-run-checkbox">
              <input
                id="open-interpreter-auto-run-checkbox"
                type="checkbox"
                css={checkboxInput}
                checked={values.autoRun}
                onChange={(e) => onChange({ autoRun: e.target.checked })}
              />
              <span style={{ fontSize: "0.88rem", fontWeight: 600, color: "#1e293b" }}>
                Auto-run code execution (auto_run: true)
              </span>
            </label>
            <p css={fieldHelpText}>
              Automatically executes generated code without interactive approval prompts. Essential for automated evaluations.
            </p>
          </div>

          {/* Safe Mode */}
          <div css={formGroup}>
            <label css={fieldLabel} htmlFor="open-interpreter-safe-mode-select">
              Safe Mode
            </label>
            <select
              id="open-interpreter-safe-mode-select"
              css={selectStyle()}
              value={values.safeMode}
              onChange={(e) => onChange({ safeMode: e.target.value as "off" | "ask" | "auto" })}
            >
              <option value="off">off (No safety prompts)</option>
              <option value="ask">ask (Prompts for confirmation)</option>
              <option value="auto">auto (Heuristic risk detection)</option>
            </select>
            <p css={fieldHelpText}>
              Open Interpreter safe mode setting for system command and shell execution controls.
            </p>
          </div>
        </div>

        {/* Field 4: System Message */}
        <div css={formGroup}>
          <label css={fieldLabel} htmlFor="open-interpreter-system-message-input">
            System Message / Instructions
          </label>
          <textarea
            id="open-interpreter-system-message-input"
            css={codeTextarea}
            placeholder="Custom instructions for Open Interpreter..."
            value={values.systemMessage || ""}
            onChange={(e) => onChange({ systemMessage: e.target.value })}
            style={{ minHeight: "80px" }}
          />
          <p css={fieldHelpText}>
            Custom system instructions to configure interpreter behavior, tool availability, and constraints.
          </p>
        </div>

        {/* Field 5: Advanced Configuration (JSON) */}
        <div css={formGroup}>
          <label css={fieldLabel}>Advanced Configuration (JSON)</label>

          {/* Examples Dropdown Box */}
          <div css={examplesBox}>
            <button
              type="button"
              css={examplesTrigger(examplesExpanded)}
              onClick={() => setExamplesExpanded((prev) => !prev)}
            >
              <span>Configuration Examples</span>
              <ChevronDownIcon
                style={{
                  transform: examplesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              />
            </button>
            {examplesExpanded && (
              <div css={examplesContent}>
                <div>
                  <div css={exampleSectionTitle}>Supported Configuration Examples</div>
                  <ul css={exampleList}>
                    {OPEN_INTERPRETER_EXAMPLES_LIST.map((item, idx) => (
                      <li key={idx} css={exampleListItem}>
                        <span>{item.desc}</span>
                        <button
                          type="button"
                          css={formatButton}
                          style={{ padding: "2px 8px", fontSize: "0.78rem" }}
                          onClick={() => handleLoadExampleJson(JSON.parse(item.code))}
                        >
                          Use Example
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* JSON Textarea Editor with Format Button */}
          <div css={jsonEditorContainer(!!errors.configJson || !!jsonFormatError)}>
            <div css={jsonEditorHeaderBar}>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                <CodeBracketsIcon />
                <span>JSON Options</span>
              </span>
              <button type="button" css={formatButton} onClick={handleFormatJson}>
                <FormatAlignIcon />
                <span>Format JSON</span>
              </button>
            </div>
            <textarea
              id="open-interpreter-config-json-textarea"
              css={codeTextarea}
              placeholder="{}"
              value={values.configJson}
              onChange={(e) => {
                onChange({ configJson: e.target.value });
                if (errors.configJson) setErrors((prev) => ({ ...prev, configJson: undefined }));
                if (jsonFormatError) setJsonFormatError(null);
              }}
            />
          </div>
          {(errors.configJson || jsonFormatError) && (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {errors.configJson || jsonFormatError}
            </p>
          )}
          <p css={fieldHelpText}>
            Advanced interpreter parameters passed as JSON (e.g. temperature, max_tokens, offline mode).
          </p>
        </div>

        {/* 3. Collapsible Shared Sections */}
        {/* Section A: Test Generation */}
        <div css={accordionSection}>
          <button
            type="button"
            css={accordionTrigger(testGenExpanded)}
            onClick={() => setTestGenExpanded((prev) => !prev)}
          >
            <span>Test Generation</span>
            <ChevronDownIcon
              style={{
                transform: testGenExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </button>
          {testGenExpanded && (
            <div css={accordionBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="open-interpreter-test-gen-instructions">
                  Instructions
                </label>
                <textarea
                  id="open-interpreter-test-gen-instructions"
                  css={textInput()}
                  rows={3}
                  placeholder="Specific test generation directives for red-team probes..."
                  value={values.testGenerationInstructions}
                  onChange={(e) => onChange({ testGenerationInstructions: e.target.value })}
                />
                <p css={fieldHelpText}>
                  Custom guidance for generating adversarial test cases targeting Open Interpreter.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section B: Delay */}
        <div css={accordionSection}>
          <button
            type="button"
            css={accordionTrigger(delayExpanded)}
            onClick={() => setDelayExpanded((prev) => !prev)}
          >
            <span>Delay</span>
            <ChevronDownIcon
              style={{
                transform: delayExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </button>
          {delayExpanded && (
            <div css={accordionBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="open-interpreter-delay-input">
                  Delay Between Requests (ms)
                </label>
                <input
                  id="open-interpreter-delay-input"
                  type="number"
                  min="0"
                  step="50"
                  css={textInput()}
                  placeholder="0"
                  value={values.delayMs}
                  onChange={(e) => onChange({ delayMs: e.target.value })}
                />
                <p css={fieldHelpText}>
                  Delay between consecutive probe executions to prevent process flooding or rate limiting.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section C: Extension Hook */}
        <div css={accordionSection}>
          <button
            type="button"
            css={accordionTrigger(extHookExpanded)}
            onClick={() => setExtHookExpanded((prev) => !prev)}
          >
            <span>Extension Hook</span>
            <ChevronDownIcon
              style={{
                transform: extHookExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
              }}
            />
          </button>
          {extHookExpanded && (
            <div css={accordionBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="open-interpreter-ext-hook-input">
                  Hook File or Module
                </label>
                <input
                  id="open-interpreter-ext-hook-input"
                  type="text"
                  css={textInput()}
                  placeholder="hooks/post_process.py"
                  value={values.extensionHook}
                  onChange={(e) => onChange({ extensionHook: e.target.value })}
                />
                <p css={fieldHelpText}>
                  Optional custom hook executed during evaluation lifecycle points.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Section D: Prompts */}
        <div css={formGroup}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label css={fieldLabel}>
              Prompts <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <button
              type="button"
              css={formatButton}
              onClick={handleAddPrompt}
              style={{ padding: "4px 10px", fontSize: "0.82rem" }}
            >
              <PlusIcon />
              <span>Add Prompt</span>
            </button>
          </div>
          <p css={fieldHelpText}>
            Must contain the <code style={{ color: "#0284c7" }}>{"{{prompt}}"}</code> variable placeholder where attack payloads are injected.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "4px" }}>
            {values.prompts.map((p, idx) => (
              <div key={idx} css={promptItemRow}>
                <input
                  type="text"
                  css={promptInput(!!errors.prompts && !p.trim())}
                  value={p}
                  onChange={(e) => handlePromptChange(idx, e.target.value)}
                  placeholder="{{prompt}}"
                />
                <button
                  type="button"
                  css={iconButton("danger")}
                  onClick={() => handleRemovePrompt(idx)}
                  disabled={values.prompts.length <= 1}
                  title={values.prompts.length <= 1 ? "At least one prompt is required" : "Remove Prompt"}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
          </div>
          {errors.prompts && (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {errors.prompts}
            </p>
          )}
        </div>
      </div>

      {/* 4. Navigation Actions Footer */}
      {!hideBottomBar && (
        <div css={actionsFooterRow}>
          <Button
            variant="default"
            style={{ border: "1px solid #cbd5e1" }}
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            style={{
              background: "#0f766e",
              color: "#ffffff",
              border: "1px solid #115e59",
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={handleNextClick}
          >
            <span>Next</span>
            <span style={{ fontSize: "1.1rem" }}>&rarr;</span>
          </Button>
        </div>
      )}
    </div>
  );
}
